import { useState, forwardRef, useImperativeHandle } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { PortalDsButton, PortalDsEmptyState, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  useGetInsertionOrdersQuery,
  useDeleteInsertionOrderMutation,
  useCloneInsertionOrderMutation,
} from 'src/redux/billing/billing.endpoints';
import { InsertionOrder } from 'src/redux/billing/billing.types';
import { getInsertionOrderDisplayStatus } from '../../utils/insertion-order-status';
import { IoBoldsignModal } from './io-boldsign-modal';
import { IoBoldsignStatusCell } from './io-boldsign-status-cell';

interface IoBoldsignTableProps {
  searchValue: string;
}

export interface IoBoldsignTableRef {
  createNew: () => void;
}

const parseLocalDate = (dateString: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateString);
};

const formatFlightDates = (startDate: Date, endDate: Date) => {
  const currentYear = new Date().getFullYear();
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  const formatDate = (date: Date, includeYear: boolean = true) => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    if (!includeYear) {
      return `${month}/${day}`;
    }
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  };
  return `${formatDate(startDate, startYear !== currentYear)}-${formatDate(endDate, endYear !== currentYear)}`;
};

export const IoBoldsignTable = forwardRef<IoBoldsignTableRef, IoBoldsignTableProps>(({ searchValue }, ref) => {
  const [isInsertionOrderModalOpen, setIsInsertionOrderModalOpen] = useState(false);
  const [selectedInsertionOrder, setSelectedInsertionOrder] = useState<InsertionOrder | undefined>(undefined);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const { data: insertionOrdersData, isLoading: insertionOrdersLoading, refetch } = useGetInsertionOrdersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [deleteInsertionOrder, { isLoading: isDeleting }] = useDeleteInsertionOrderMutation();
  const [cloneInsertionOrder, { isLoading: isCloning }] = useCloneInsertionOrderMutation();

  const handleCreateNewInsertionOrder = () => {
    setSelectedInsertionOrder(undefined);
    setModalMode('create');
    setIsInsertionOrderModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    createNew: handleCreateNewInsertionOrder,
  }));

  const insertionOrders = (insertionOrdersData || [])
    .slice()
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  const handleInsertionOrderClick = (order: InsertionOrder) => {
    setSelectedInsertionOrder(order);
    const status = getInsertionOrderDisplayStatus(order);
    if (status === 'draft') {
      setModalMode('edit');
    } else {
      setModalMode('view');
    }
    setIsInsertionOrderModalOpen(true);
  };

  const filteredData = insertionOrders.filter((order) => {
    const matchesSearch = order.advertiser_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
      order.campaign_name?.toLowerCase().includes(searchValue.toLowerCase()) ||
      order.agent_name?.toLowerCase().includes(searchValue.toLowerCase());

    return matchesSearch;
  });

  const isEmpty = filteredData.length === 0;
  const hasSearch = searchValue.trim() !== '';

  const handleDeleteInsertionOrder = async(id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteInsertionOrder(id).unwrap();
      setShowDeleteDialog(false);
      setItemToDelete(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete insertion order:', error);
    }
  };

  const handleDeleteClick = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToDelete(id);
    setShowDeleteDialog(true);
  };

  const handleCloneInsertionOrder = async(id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await cloneInsertionOrder(id).unwrap();
    } catch (error) {
      console.error('Failed to clone insertion order:', error);
    }
  };

  if (insertionOrdersLoading) {
    return (
      <div className={cn(portalDs.tableContainer, 'flex items-center justify-center py-20')}>
        <p className={portalDs.bodySm}>
          Loading insertion orders...
        </p>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className={portalDs.tableContainer}>
        <PortalDsEmptyState
          title={hasSearch ? 'No insertion orders found' : 'No insertion orders yet'}
          description={
            hasSearch
              ? 'Try changing your search criteria.'
              : 'Create your first insertion order to get started.'
          }
        />
      </div>
    );
  }

  return (
    <>
      <div className={portalDs.tableContainer}>
        <Table>
          <TableHeader>
            <TableRow className={portalDs.tableHead}>
              <TableHead className={portalDs.tableHeadCell}>
                Last Updated
              </TableHead>
              <TableHead className={portalDs.tableHeadCell}>
                Advertiser
              </TableHead>
              <TableHead className={portalDs.tableHeadCell}>
                Campaign
              </TableHead>
              <TableHead className={portalDs.tableHeadCell}>
                Flight Dates
              </TableHead>
              <TableHead className={portalDs.tableHeadCell}>
                Owner
              </TableHead>
              <TableHead className={cn(portalDs.tableHeadCell, 'text-center')}>
                Status
              </TableHead>
              <TableHead className={cn(portalDs.tableHeadCell, 'w-[44px]')} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredData.map((order) => (
              <TableRow
                key={order.id}
                className={cn(portalDs.tableBodyRow, portalDs.tableBodyRowInteractive, 'group')}
                onClick={() => handleInsertionOrderClick(order)}
              >
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {new Date(order.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </TableCell>
                <TableCell className={portalDs.tableCell}>
                  {order.advertiser_name || 'N/A'}
                </TableCell>
                <TableCell className={portalDs.tableCell}>
                  {order.campaign_name || 'N/A'}
                </TableCell>
                <TableCell className={portalDs.tableCell}>
                  {formatFlightDates(parseLocalDate(order.start_date), parseLocalDate(order.end_date))}
                </TableCell>
                <TableCell className={portalDs.tableCell}>
                  {order.agent_name}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, 'text-center')}>
                  <IoBoldsignStatusCell order={order} />
                </TableCell>
                <TableCell
                  className={portalDs.tableCell}
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <PortalDsButton
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        disabled={isDeleting || isCloning}
                        aria-label="Insertion order actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </PortalDsButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={(e) => handleCloneInsertionOrder(order.id, e)}
                        disabled={isCloning}
                      >
                        {isCloning ? 'Cloning...' : 'Clone'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer text-[#DC2626] focus:text-[#DC2626]"
                        onClick={(e) => handleDeleteClick(order.id, e)}
                        disabled={isDeleting}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
      >
        <DialogContent className={cn(portalDs.modal, 'gap-0 p-0')}>
          <DialogHeader className={cn(portalDs.modalHeader, 'flex-col items-start gap-2')}>
            <DialogTitle className={portalDs.modalTitle}>
              Delete Insertion Order
            </DialogTitle>
            <DialogDescription className={portalDs.sheetDescription}>
              Are you sure you want to delete this insertion order? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className={cn(portalDs.modalFooter, 'sm:space-x-0')}>
            <PortalDsButton
              variant="secondary"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              variant="danger"
              onClick={(e) => itemToDelete && handleDeleteInsertionOrder(itemToDelete, e)}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <IoBoldsignModal
        open={isInsertionOrderModalOpen}
        onOpenChange={setIsInsertionOrderModalOpen}
        initialData={selectedInsertionOrder}
        mode={modalMode}
        onSuccess={() => refetch()}
      />
    </>
  );
});

IoBoldsignTable.displayName = 'IoBoldsignTable';
